import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { MODE_LABELS, type TtsMode } from './contracts';
import { type MimoStorage, mimoStorage } from './storage';
import { MimoElement } from './RegisteredElement';
import styles from './mimo.module.css';

export interface TtsModeSelectorProps {
  value?: TtsMode;
  onChange?: (mode: TtsMode) => void;
  userId?: string;
  conversationId?: string;
  persist?: boolean;
  disabled?: boolean;
  storage?: MimoStorage;
  onPersistenceError?: () => void;
}
export function TtsModeSelector({ value, onChange, userId, conversationId, persist = true, disabled, storage = mimoStorage, onPersistenceError }: TtsModeSelectorProps): ReactElement {
  const [local, setLocal] = useState<TtsMode>('off');
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const scope = JSON.stringify([userId ?? '', conversationId ?? null]);
  const callbacks = useRef({ onChange, onPersistenceError });
  callbacks.current = { onChange, onPersistenceError };
  const writes = useRef<Promise<unknown>>(Promise.resolve());
  useEffect(() => {
    let current = true;
    if (!persist || !userId) { setLocal('off'); setLoadedScope(scope); return; }
    void writes.current.then(() => storage.readMode(userId, conversationId)).then((mode) => {
      if (current) { setLocal(mode); setLoadedScope(scope); callbacks.current.onChange?.(mode); }
    }).catch(() => { if (current) { setLoadedScope(scope); callbacks.current.onPersistenceError?.(); } });
    return () => { current = false; };
  }, [storage, persist, userId, conversationId, scope]);
  const selected = loadedScope === scope ? value ?? local : 'off';
  return <MimoElement meta="selector" instance={scope} as="select" className={styles.selectorSelect}
    aria-label="聊天语音模式" title="语音回复模式" value={selected}
    disabled={(disabled ?? false) || loadedScope !== scope}
    onChange={(event) => {
      const mode = event.target.value as TtsMode;
      setLocal(mode); onChange?.(mode);
      if (persist && userId) {
        writes.current = writes.current.then(() => storage.saveMode(userId, conversationId, mode)).catch(() => callbacks.current.onPersistenceError?.());
      }
    }}>
    {(Object.keys(MODE_LABELS) as TtsMode[]).map((mode) => <option key={mode} value={mode}>语音：{MODE_LABELS[mode]}</option>)}
  </MimoElement>;
}
